import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import {
  fetchUserReading,
  fetchUserUsage,
  listUsers,
  setUserBudget,
  type AdminUser,
  type SessionStats,
  type Usage,
  type UserReading,
} from "../api";
import BackLink from "../components/BackLink";
import { useT } from "../i18n";
import { useSession } from "../session";
import { formatMicros, parseAmount } from "../studio/money";
import { periodLabel, UsageState } from "./Usage";

/**
 * AdminUserUsage is one account's usage page, as a platform admin sees it: the
 * same receipt the account sees for itself.
 */
export default function AdminUserUsage() {
  const { userID = "" } = useParams();
  const { me, isPending } = useSession();
  const t = useT();
  const isAdmin = !!me?.user.platform_admin;

  // The list is usually cached from the page this was opened from, so the name
  // costs nothing; a direct link fetches it.
  const users = useQuery({
    queryKey: ["admin", "users"],
    queryFn: listUsers,
    enabled: isAdmin,
  });
  const usage = useQuery({
    queryKey: ["admin", "usage", userID],
    queryFn: () => fetchUserUsage(userID),
    enabled: isAdmin && !!userID,
  });

  if (!isPending && !isAdmin) return <Navigate to="/" replace />;

  const user = users.data?.items.find((u) => u.id === userID);

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <BackLink to="/admin">{t.admin.back}</BackLink>
      <header className="mt-4 mb-8">
        <h1
          dir="auto"
          data-testid="admin-usage-title"
          className="text-[1.75rem] leading-tight font-semibold tracking-tight"
        >
          {user ? user.name || user.email : t.usage.title}
        </h1>
        <p className="mt-1 text-sm text-muted">
          {[
            user?.name ? user.email : null,
            user?.role ? (t.admin.roles[user.role] ?? user.role) : null,
            usage.data ? periodLabel(usage.data, t) : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </header>
      <UsageState
        isPending={isPending || usage.isPending}
        isError={usage.isError}
        onRetry={() => void usage.refetch()}
        data={usageWithCeiling(usage.data, user)}
      />
      {user && <LimitControl user={user} />}
      {isAdmin && userID && <Records userID={userID} />}
    </main>
  );
}

/**
 * Records is what the Prolific test measures per account (ADR-0146): minutes
 * in each book, and questions by kind — typed against each one-tap action.
 */
function Records({ userID }: { userID: string }) {
  const t = useT();
  const reading = useQuery({
    queryKey: ["admin", "reading", userID],
    queryFn: () => fetchUserReading(userID),
  });
  if (!reading.data) return null;
  const { books, questions, sessions } = reading.data;
  return (
    <section data-testid="admin-records" className="mt-8">
      <h2 className="mb-2 text-sm font-semibold">{t.admin.recordsTitle}</h2>
      {books.length === 0 ? (
        <p className="text-sm text-muted">{t.admin.recordsNone}</p>
      ) : (
        <ul className="divide-y divide-line rounded-card border border-line bg-surface text-sm">
          {books.map((b) => (
            <li key={b.book_id} className="flex justify-between gap-4 px-4 py-2">
              <span dir="auto" className="truncate">
                {b.title}
              </span>
              <span className="shrink-0 tabular-nums">
                {t.admin.minutes(Math.round(b.seconds / 60))}
              </span>
            </li>
          ))}
        </ul>
      )}
      <h2 className="mt-6 mb-2 text-sm font-semibold">{t.admin.questionsTitle}</h2>
      <ul className="grid grid-cols-4 gap-2 text-center text-sm" data-testid="admin-question-kinds">
        {["typed", "meaning", "explain", "simpler"].map((k) => (
          <li key={k} className="rounded-card border border-line bg-surface px-2 py-2">
            <p className="text-lg font-semibold tabular-nums">{questions[k] ?? 0}</p>
            <p className="text-xs text-muted">{t.admin.questionKinds[k]}</p>
          </li>
        ))}
      </ul>
      {sessions && <SessionsRecord sessions={sessions} />}
    </section>
  );
}

const ENDED_BY = ["switched_away", "left", "idle"];

/**
 * SessionsRecord is the account's reading sessions (ADR-0218): count, median
 * and longest over every book, how they ended, then the same per book.
 */
export function SessionsRecord({ sessions }: { sessions: UserReading["sessions"] }) {
  const t = useT();
  const { overall, books } = sessions;
  return (
    <div data-testid="admin-sessions">
      <h2 className="mt-6 mb-2 text-sm font-semibold">{t.admin.sessionsTitle}</h2>
      {overall.count === 0 ? (
        <p className="text-sm text-muted">{t.admin.sessionsNone}</p>
      ) : (
        <>
          <ul
            className="grid grid-cols-3 gap-2 text-center text-sm"
            data-testid="admin-sessions-overall"
          >
            {[
              [t.admin.sessionsCount, String(overall.count)],
              [t.admin.sessionsMedian, t.admin.duration(overall.median_seconds)],
              [t.admin.sessionsLongest, t.admin.duration(overall.longest_seconds)],
            ].map(([label, value]) => (
              <li key={label} className="rounded-card border border-line bg-surface px-2 py-2">
                <p className="text-lg font-semibold tabular-nums">{value}</p>
                <p className="text-xs text-muted">{label}</p>
              </li>
            ))}
          </ul>
          <EndedBy stats={overall} testid="admin-sessions-ended-by" />
          <ul className="mt-3 divide-y divide-line rounded-card border border-line bg-surface text-sm">
            {books.map((b) => (
              <li key={b.book_id} data-testid="admin-sessions-book" className="px-4 py-2">
                <div className="flex justify-between gap-4">
                  <span dir="auto" className="truncate">
                    {b.title}
                  </span>
                  <span className="shrink-0 tabular-nums">
                    {t.admin.sessionsBook(b.count, t.admin.duration(b.median_seconds))}
                  </span>
                </div>
                <EndedBy stats={b} />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/** EndedBy is how a set of sessions ended, as one muted line. */
function EndedBy({ stats, testid }: { stats: SessionStats; testid?: string }) {
  const t = useT();
  return (
    <p data-testid={testid} className="mt-1 text-xs text-muted tabular-nums">
      {ENDED_BY.map((k) => `${t.admin.sessionsEndedBy[k]} ${stats.ended_by[k] ?? 0}`).join(" · ")}
    </p>
  );
}

/**
 * The amounts a limit is usually set to, plus zero — which is a limit, and the
 * way to stop an account mid-month (ADR-0082).
 *
 * Chips rather than a dropdown or a number pad: this panel is driven from a
 * phone, and the common case should be one tap. `Other` is there for the
 * amount the list does not hold.
 */
const PRESET_MICROS = [0, 1_000_000, 5_000_000, 25_000_000, 50_000_000, 100_000_000];

/**
 * usageWithCeiling makes the **user list** this screen's one source for the
 * ceiling (ADR-0090), by laying the list's copy over the receipt body before
 * it is rendered. The usage endpoint's own `limit_*` fields are correct and
 * are simply not what this screen draws.
 *
 * **Deriving beats synchronising, and this bug is why.** The two are separate
 * fetches of one fact, so keeping them equal means keeping two caches in step
 * — and a usage response that left the server *before* the write can land
 * *after* it and become the cached value, with nothing able to displace it
 * (no query in this client passes an `AbortSignal`, so React Query cannot
 * cancel the request it would need to supersede: `docs/tech-debt.md`). That
 * failed four times over three days and was diagnosed wrong three times.
 * Reading one source removes the race rather than narrowing it: a stale usage
 * body can still land here, and its ceiling is ignored when it does.
 *
 * The list is the right source because it is the one the write answers: the
 * `PUT` returns the account DTO and the mutation puts it straight into the
 * list's cache, so the receipt moves in the same render as the chip.
 */
export function usageWithCeiling(usage?: Usage, user?: AdminUser): Usage | undefined {
  if (!usage) return undefined;
  // Absent is not zero: an account with no library has no ceiling, and the
  // handler 404s rather than sending a body, so this cannot be reached with a
  // usage body in hand. Falling back to the body keeps that honest either way.
  if (!user || user.limit_micros === undefined || user.limit_usd === undefined) return usage;
  return {
    ...usage,
    limit_micros: user.limit_micros,
    limit_usd: user.limit_usd,
    remaining_micros: user.remaining_micros ?? usage.remaining_micros,
    remaining_usd: user.remaining_usd ?? usage.remaining_usd,
  };
}

/**
 * LimitControl moves the ceiling this account spends against — its own
 * library's monthly limit, which is what a spend is actually checked against
 * (ADR-0082). It says whose limit it is, because the word stays true if that
 * library ever gains a second member.
 */
function LimitControl({ user }: { user: AdminUser }) {
  const t = useT();
  const client = useQueryClient();
  const [typed, setTyped] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);

  const limit = useMutation({
    mutationFn: (micros: number) => setUserBudget(user.id, micros),
    // The ceiling is shown twice on this screen — as the pressed chip, and in
    // the receipt above it — so it has ONE source, and both views read it
    // (ADR-0090). The source is the user list, because that is what this write
    // answers with.
    //
    // Three earlier attempts kept two caches in step instead — two
    // invalidations, then a write into both — and each failed the same way.
    // Keeping two copies equal cannot be made safe here; having one copy can.
    onSuccess: (updated) => {
      // The screen's one source for the ceiling: the chip reads it, and so does
      // the receipt above, through `usageWithCeiling`. One write moves both.
      //
      // The usage query is deliberately NOT written here. It carries a ceiling
      // too, and keeping a second copy in step is what failed four times — see
      // `usageWithCeiling`.
      client.setQueryData<{ items: AdminUser[] }>(["admin", "users"], (previous) =>
        previous
          ? { ...previous, items: previous.items.map((u) => (u.id === updated.id ? updated : u)) }
          : previous,
      );
      setTyped(null);
      setInvalid(false);
    },
  });

  const name = user.name || user.email;
  // Absent is not zero: an account with no library has no ceiling to move.
  if (user.limit_micros === undefined) {
    return (
      <p data-testid="admin-limit-none" className="mt-8 text-sm text-muted">
        {t.admin.limitNone}
      </p>
    );
  }

  const current = user.limit_micros;
  const amounts = PRESET_MICROS.includes(current)
    ? PRESET_MICROS
    : [...PRESET_MICROS, current].sort((a, b) => a - b);
  const chip = (selected: boolean) =>
    `rounded-full border px-3 py-1.5 text-sm transition-colors disabled:opacity-40 ${
      selected
        ? "border-sage bg-sage-soft font-medium text-sage-deep"
        : "border-line text-muted hover:text-ink"
    }`;

  return (
    <section data-testid="admin-limit" className="mt-8">
      <h2 className="mb-1 text-sm font-medium text-ink-soft">{t.admin.limitTitle}</h2>
      <p className="mb-3 text-xs text-muted">{t.admin.limitWhat}</p>
      <fieldset className="flex flex-wrap items-center gap-1.5">
        <legend className="sr-only">{t.admin.limitFor(name)}</legend>
        {amounts.map((micros) => (
          <button
            key={micros}
            type="button"
            disabled={limit.isPending}
            aria-pressed={typed === null && micros === current}
            data-testid={`admin-limit-${micros}`}
            onClick={() => limit.mutate(micros)}
            className={chip(typed === null && micros === current)}
          >
            {/* An amount the client chose rather than one the server sent, so
                it is formatted by the client's one formatter, and isolated
                because a chip sits in a Persian page. */}
            {micros === 0 ? t.admin.limitStop : <bdi dir="ltr">{formatMicros(micros)}</bdi>}
          </button>
        ))}
        <button
          type="button"
          disabled={limit.isPending}
          aria-pressed={typed !== null}
          data-testid="admin-limit-other"
          onClick={() => setTyped(typed === null ? "" : null)}
          className={chip(typed !== null)}
        >
          {t.admin.limitOther}
        </button>
      </fieldset>

      {typed !== null && (
        <form
          className="mt-3 flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            // Parsed by the module that formats, so the two agree about cents,
            // and after Persian digits are normalised (ADR-0082).
            const micros = parseAmount(typed);
            if (micros === null) {
              setInvalid(true);
              return;
            }
            limit.mutate(micros);
          }}
        >
          <label className="sr-only" htmlFor="admin-limit-amount">
            {t.admin.limitOtherLabel}
          </label>
          <input
            id="admin-limit-amount"
            data-testid="admin-limit-amount"
            dir="ltr"
            inputMode="decimal"
            autoComplete="off"
            value={typed}
            onChange={(e) => {
              setTyped(e.target.value);
              setInvalid(false);
            }}
            className="w-28 rounded-control border border-line bg-surface px-2 py-1.5 text-sm"
          />
          <button
            type="submit"
            disabled={limit.isPending}
            data-testid="admin-limit-save"
            className="rounded-control border border-line px-3 py-1.5 text-sm disabled:opacity-40"
          >
            {t.admin.limitSave}
          </button>
        </form>
      )}

      {invalid && (
        <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">
          {t.admin.limitInvalid}
        </p>
      )}
      {limit.isError && (
        <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">
          {limit.error instanceof Error ? limit.error.message : t.admin.limitFailed}
        </p>
      )}
    </section>
  );
}
